import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { getAuthSessionEpoch, useAuthStore } from '@/stores/authStore';
import { InstitutionalPrivateReadError, readInstitutionalPrivateSnapshot,
  type InstitutionalPrivateSnapshotView } from '@/lib/polymarketUs/institutionalPrivateClient';
import type { UsInstitutionalDeskAccount } from '@/views/TraderDetail/usManagerReads';

const subscribe = (notify: () => void) => useAuthStore.subscribe(notify);
type Loaded = { key: string; data: InstitutionalPrivateSnapshotView | null; busy: boolean; failed: boolean };
/** Existing account inventory supplies the selected desk. Expansion reads only
 * saved evidence; the separate explicit action owns the venue snapshot. */
export function useInstitutionalPrivateSnapshot(account: UsInstitutionalDeskAccount | null, orgKey: string, refreshKey: number) {
  const epoch = useSyncExternalStore(subscribe, getAuthSessionEpoch);
  const userId = useAuthStore(s => s.user?.id);
  const accountId = account && userId && account.deskUserIds.includes(userId) ? account.id : '';
  const identity = `${epoch}|${orgKey}|${accountId}`;
  const selection = useRef({ identity, generation: 0 });
  if (selection.current.identity !== identity) selection.current = { identity, generation: selection.current.generation + 1 };
  // Returning to an earlier account is new demand; old disclosure state cannot
  // reopen or expose its previous snapshot after an intervening access change.
  const key = `${identity}|${selection.current.generation}`;
  const [openKey, setOpenKey] = useState<string | null>(null);
  const open = accountId !== '' && openKey === key;
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const sequence = useRef(0), controller = useRef<AbortController | null>(null), busy = useRef(false);
  const currentKey = useRef(key); currentKey.current = key;
  const currentOpen = useRef(open); currentOpen.current = open;
  const current = open && loaded?.key === key ? loaded : null;

  async function request(refresh?: InstitutionalPrivateSnapshotView['scope']) {
    if (!accountId || !open || busy.current) return;
    controller.current?.abort();
    const own = new AbortController(); controller.current = own;
    const seq = ++sequence.current; busy.current = true;
    setLoaded(prev => ({ key, data: prev?.key === key ? prev.data : null, busy: true, failed: false }));
    const valid = () => seq === sequence.current && currentKey.current === key && currentOpen.current && getAuthSessionEpoch() === epoch;
    try {
      const data = await readInstitutionalPrivateSnapshot(accountId, AbortSignal.any([own.signal, AbortSignal.timeout(40_000)]), refresh);
      if (valid()) setLoaded({ key, data, busy: false, failed: data.lastAttempt?.state === 'unavailable' });
    } catch (error) {
      if (valid()) setLoaded(prev => ({ key, busy: false, failed: true,
        data: error instanceof InstitutionalPrivateReadError && error.accessChanged ? null : prev?.key === key ? prev.data : null }));
    } finally { if (valid()) busy.current = false; }
  }
  useEffect(() => {
    if (open) void request();
    // This counter intentionally invalidates every outstanding request on cleanup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => { sequence.current++; busy.current = false; controller.current?.abort(); };
    // Identity and demand govern the request; no polling or callback loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, open, refreshKey]);
  return { eligible: accountId !== '', open, toggle: () => setOpenKey(open ? null : key), data: current?.data ?? null,
    busy: open && (current === null || current.busy), failed: current?.failed ?? false,
    refresh: () => { if (current?.data?.canRefresh && !current.busy) void request(current.data.scope); },
    readSaved: () => { if (!busy.current) void request(); } };
}
